import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./OneTimePaymentPage.css";

const OneTimePaymentPage = ({ user }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("jwtToken");
    const tokenType = localStorage.getItem("tokenType") || "Bearer";

    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `${tokenType} ${token}`;
    }

    return headers;
  };

  useEffect(() => {
    fetchOneTimeProducts();
  }, []);

  const fetchOneTimeProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const endpoint = `${baseUrl}/api/payments/products/one-time`;

      const response = await fetch(endpoint, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      setProducts(data);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (product) => {
    if (!user || !user.id) {
      setError("Please login to make a purchase");
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const endpoint = `${baseUrl}/api/payments/create-checkout-session`;

      // Get planId from product metadata or product name
      // You'll need to add planId to your product metadata in Stripe
      const planId = product.planId || "1"; // Adjust based on your data structure
      const basePath = process.env.REACT_APP_BASE_URL || '';

      const payload = {
        userId: user.id,
        productId: product.id,
        planId: planId,
        quantity: 1,
        returnUrl: window.location.origin + basePath,
      };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to create checkout session: ${response.status}`);
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL provided");
      }
    } catch (err) {
      console.error("Error processing purchase:", err);
      setError("Failed to process purchase. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (loading && products.length === 0) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading products...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-5 mb-5">
      <div className="mb-4">
        <h1>Purchase Products</h1>
        <p className="text-muted">Browse and purchase one-time items</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {products.length === 0 ? (
        <Alert variant="info">No products available at the moment.</Alert>
      ) : (
        <Row className="g-4">
          {products.map((product) => (
            <Col key={product.id} md={6} lg={4}>
              <Card className="product-card h-100 shadow-sm">
                {product.image && (
                  <Card.Img
                    variant="top"
                    src={product.image}
                    alt={product.name}
                    className="product-image"
                  />
                )}
                <Card.Body className="d-flex flex-column">
                  <Card.Title>{product.name}</Card.Title>
                  <Card.Text className="text-muted flex-grow-1">
                    {product.description}
                  </Card.Text>

                  <div className="mt-auto">
                    <div className="price-section mb-3">
                      <h4 className="text-primary">
                        {formatPrice(product.price, product.currency)}
                      </h4>
                    </div>

                    <Button
                      variant="primary"
                      className="w-100"
                      onClick={() => handlePurchase(product)}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Processing...
                        </>
                      ) : (
                        "Buy Now"
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default OneTimePaymentPage;